import { supabase } from '../lib/supabase';

const NHTSA_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles/decodevin';

/**
 * Decode a VIN using:
 * 1. Local DB cache (vehicle_specs_cache)
 * 2. NHTSA public API (online fallback)
 */
export const vinDecodeService = {
  /**
   * Get cached VIN specs from DB
   */
  async getFromCache(vin) {
    try {
      const { data, error } = await supabase
        ?.from('vehicle_specs_cache')
        ?.select('*')
        ?.eq('vin', vin)
        ?.maybeSingle();
      if (error) return null;
      return data || null;
    } catch {
      return null;
    }
  },

  /**
   * Save VIN specs to DB cache
   */
  async saveToCache(vin, specs) {
    try {
      await supabase?.from('vehicle_specs_cache')?.upsert({
        vin,
        make: specs?.make || null,
        model: specs?.model || null,
        year: specs?.year || null,
        engine: specs?.engine || null,
        fuel_type: specs?.fuelType || null,
        trim: specs?.trim || null,
        raw_json: specs?.rawJson || null,
        updated_at: new Date()?.toISOString()
      }, { onConflict: 'vin' });
    } catch (err) {
      console.error('[VIN_CACHE_SAVE_FAIL]', err);
    }
  },

  /**
   * Decode VIN via NHTSA public API
   */
  async decodeFromNHTSA(vin) {
    const url = `${NHTSA_URL}/${encodeURIComponent(vin)}?format=json`;
    const response = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!response?.ok) throw new Error(`NHTSA HTTP ${response?.status}`);
    const json = await response?.json();
    const results = json?.Results || [];

    const getValue = (variable) =>
      results?.find(r => r?.Variable === variable)?.Value || '';

    const make = getValue('Make');
    const model = getValue('Model');
    const yearStr = getValue('Model Year');
    const year = yearStr ? parseInt(yearStr) : null;
    const engine = getValue('Displacement (L)')
      ? `${getValue('Displacement (L)')}L ${getValue('Engine Configuration') || ''}`?.trim()
      : getValue('Engine Model') || '';
    const fuelType = getValue('Fuel Type - Primary') || '';
    const trim = getValue('Trim') || '';

    return { make, model, year, engine, fuelType, trim, rawJson: json };
  },

  /**
   * Main decode function:
   * 1. Check cache
   * 2. Call NHTSA if not cached
   * 3. Save to cache on success
   * Returns null if VIN decode fails (non-blocking)
   */
  async decodeVin(vin) {
    if (!vin || vin?.length !== 17) return null;

    console.log('[VIN_DECODE_START]', vin);

    // 1. Check cache first
    const cached = await this.getFromCache(vin);
    if (cached) {
      console.log('[VIN_DECODE_SUCCESS] (from cache)', vin, cached);
      return {
        make: cached?.make || '',
        model: cached?.model || '',
        year: cached?.year || null,
        engine: cached?.engine || '',
        fuelType: cached?.fuel_type || '',
        trim: cached?.trim || '',
        fromCache: true
      };
    }

    // 2. Call NHTSA
    try {
      const specs = await this.decodeFromNHTSA(vin);

      // Validate we got useful data
      if (!specs?.make && !specs?.model) {
        console.warn('[VIN_DECODE_FAIL] NHTSA returned empty data for VIN:', vin);
        return null;
      }

      console.log('[VIN_DECODE_SUCCESS] (from NHTSA)', vin, specs);

      // 3. Save to cache
      await this.saveToCache(vin, specs);

      return {
        make: specs?.make || '',
        model: specs?.model || '',
        year: specs?.year || null,
        engine: specs?.engine || '',
        fuelType: specs?.fuelType || '',
        trim: specs?.trim || '',
        fromCache: false
      };
    } catch (err) {
      console.warn('[VIN_DECODE_FAIL]', vin, err?.message);
      return null;
    }
  }
};
